/**
 * Environment detection and configuration utilities
 */

export type Environment = "development" | "test" | "staging" | "production";

/**
 * Check if a value is a valid environment
 */
export function isValidEnvironment(value: string): value is Environment {
  return (
    value === "development" || value === "test" || value === "staging" || value === "production"
  );
}

export interface EnvironmentConfig {
  name: Environment;
  displayName: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  icon: string;
  description: string;
}

/**
 * Get current environment based on Vite mode and other indicators
 */
export function getCurrentEnvironment(): Environment {
  const mode = import.meta.env.MODE;
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;

  // Check for explicit environment variables
  if (import.meta.env.VITE_ENV) {
    const envValue = import.meta.env.VITE_ENV as string;
    // Validate that the environment value is one of the valid options
    if (isValidEnvironment(envValue)) {
      return envValue;
    }
    console.warn(`Invalid VITE_ENV value: ${envValue}, falling back to production`);
  }

  // Check Vite mode
  if (mode === "development" || isDev) {
    return "development";
  }

  if (mode === "test") {
    return "test";
  }

  if (mode === "staging") {
    return "staging";
  }

  if (mode === "production" || isProd) {
    return "production";
  }

  // Fallback based on hostname for deployed environments
  const hostname = window.location.hostname;

  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return "development";
  }

  if (hostname.includes("test") || hostname.includes("testing")) {
    return "test";
  }

  if (hostname.includes("staging") || hostname.includes("stage")) {
    return "staging";
  }

  // Default to production for unknown environments
  return "production";
}

/**
 * Environment configurations with styling and metadata
 */
export const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  development: {
    name: "development",
    displayName: "Development",
    color: "#8B5CF6", // violet-500
    bgColor: "#F5F3FF", // violet-50
    borderColor: "#8B5CF6", // violet-500
    textColor: "#5B21B6", // violet-800
    icon: "⚡",
    description: "Local development environment",
  },
  test: {
    name: "test",
    displayName: "Test",
    color: "#F59E0B", // amber-500
    bgColor: "#FFFBEB", // amber-50
    borderColor: "#F59E0B", // amber-500
    textColor: "#92400E", // amber-800
    icon: "🧪",
    description: "Testing environment",
  },
  staging: {
    name: "staging",
    displayName: "Staging",
    color: "#3B82F6", // blue-500
    bgColor: "#EFF6FF", // blue-50
    borderColor: "#3B82F6", // blue-500
    textColor: "#1E40AF", // blue-800
    icon: "🎭",
    description: "Pre-production staging environment",
  },
  production: {
    name: "production",
    displayName: "Production",
    color: "#10B981", // green-500
    bgColor: "#ECFDF5", // green-50
    borderColor: "#10B981", // green-500
    textColor: "#065F46", // green-800
    icon: "🚀",
    description: "Live production environment",
  },
};

/**
 * Get environment configuration for current environment
 */
export function getCurrentEnvironmentConfig(): EnvironmentConfig {
  const currentEnv = getCurrentEnvironment();
  const config = environmentConfigs[currentEnv];

  // Fallback to production config if environment is not found
  if (!config) {
    console.warn(
      `Environment configuration not found for: ${currentEnv}, falling back to production`,
    );
    return environmentConfigs.production;
  }

  return config;
}

/**
 * Check if current environment is development
 */
export function isDev(): boolean {
  return getCurrentEnvironment() === "development";
}

/**
 * Check if current environment is test
 */
export function isTest(): boolean {
  return getCurrentEnvironment() === "test";
}

/**
 * Check if current environment is staging
 */
export function isStaging(): boolean {
  return getCurrentEnvironment() === "staging";
}

/**
 * Check if current environment is production
 */
export function isProd(): boolean {
  return getCurrentEnvironment() === "production";
}

/**
 * Check if current environment is non-production (dev, test, staging)
 */
export function isNonProduction(): boolean {
  return !isProd();
}

/**
 * Get environment-specific API URL
 */
export function getEnvironmentApiUrl(): string {
  const env = getCurrentEnvironment();
  const apiUrl = import.meta.env.VITE_API_URL;

  // Return configured API URL or fallback based on environment
  if (apiUrl) {
    return apiUrl;
  }

  // Fallback URLs based on environment
  switch (env) {
    case "development":
      return "http://localhost:3000/api";
    case "test":
      return "https://api-test.example.com";
    case "staging":
      return "https://api-staging.example.com";
    case "production":
      return "https://api.example.com";
    default:
      return "https://api.example.com";
  }
}

/**
 * Log environment information to console
 */
export function logEnvironmentInfo(): void {
  const config = getCurrentEnvironmentConfig();

  // Additional safety check
  if (!config) {
    console.error("Failed to get environment configuration");
    return;
  }
}
